/**
 * Marketing section feature data — single source of truth.
 * Used by GoFeaturesSection and potentially other marketing pages.
 */

export interface FeatureItem {
  iconSrc: string
  title: string
  description: string
}

export const goFeatures: FeatureItem[] = [
  {
    iconSrc: "/marketing/go/icons/ai.svg",
    title: "AI Assistant",
    description: `Tired of the daily "What should we eat?" question? To To-Do's AI gives you the answer instantly — a recipe plus a ready-to-use shopping list.\n\nLet AI help organize your day, suggest priorities, and turn ideas into actionable tasks with a single tap.`,
  },
  {
    iconSrc: "/marketing/go/icons/collaboration.svg",
    title: "Easy to Share",
    description: `Collaboration should be smooth, not complicated. Whether you're planning a birthday party with your partner, organizing a shopping list for the family, or leading a project at work, you can easily invite others to collaborate in real time.\n\nSee changes instantly as your team works together.`,
  },
  {
    iconSrc: "/marketing/go/icons/offline.svg",
    title: "Works Offline",
    description: `On a flight or commuting underground with limited signal? To To-Do always works — just as fast as when you're online. Everything is saved locally and syncs automatically once you're back.\n\nLife doesn't stop when your internet does. Neither should your productivity.`,
  },
  {
    iconSrc: "/marketing/go/icons/cross-plattform.svg",
    title: "Available Everywhere",
    description: `Your family and colleagues aren't all on the same platform. That's why To To-Do works on all big platforms and the web. A seamless experience, for everyone.\n\nOne unified experience across Windows, Mac, iOS, Android, and web — with everything in perfect sync.`,
  },
  {
    iconSrc: "/marketing/go/icons/one-app.svg",
    title: "Intuitive Design",
    description: `The interface is designed to reduce friction and make life easier for your brain. Using To To-Do should feel natural, fun, and effortless—never complicated.\n\nChoose the colors that fit your style and personalize the app to match your needs.`,
  },
  {
    iconSrc: "/marketing/go/icons/community.svg",
    title: "Built with you",
    description: `Our mission is to be the best productivity app in our niche and let all our users request features and discuss their ideas, needs and problems on our Discord server.\n\nWe believe in building together with our community — your feedback shapes our future.`,
  },
]

export interface TestimonialItem {
  stars: number
  title: string
  description: string
  image: string
  name: string
  role: string
}

export const testimonials: TestimonialItem[] = [
  {
    stars: 5,
    title: "A lifesaver for a business mom",
    description: `This to-do app helps me plan content and stay on top of every day tasks, making my life so much easier. I share lists with my husband and kids to sync family planning, what needs to be done at the house and with the kids.\n\nThe AI assistant really helps me create more detailed tasks and meal plans in seconds. I really cannot imagine my life without it!`,
    image: "/marketing/images/aida_400x400.jpg",
    name: "Aida Begovic",
    role: "Entrepreneur & Business mom",
  },
  {
    stars: 5,
    title: "A real game changer for us",
    description: `My partner and I have used To To-Do for over a year to manage life with our daughter, from activities to shopping lists. With one shared list and calendar, we both add what's needed and stay on the same page. It saves time, avoids confusion, and always having the updated list on our phones is gold.`,
    image: "/marketing/images/patrik_200x200.jpg",
    name: "Patrik Hermansson",
    role: "Hard working dad",
  },
  {
    stars: 5,
    title: "Knocks down its competitors",
    description: `To To-Do knocked out the competition. For me and my wife, it's been life-changing. We switched from Microsoft To Do and haven't looked back. To To-Do is not only easier to use, it's packed with features that actually help us manage life together. From shopping and packing lists to wishlists, our family now depends on it daily.`,
    image: "/marketing/images/david_200x200.jpeg",
    name: "David Fahlander",
    role: "Business owner",
  },
]

export interface QaItem {
  question: string
  answer: string
}

export const qaItems: QaItem[] = [
  { question: "Is it a shopping list?", answer: "Yes." },
  { question: "Is it a reminder?", answer: "Yes." },
  { question: "Is it a to-do app?", answer: "Yes." },
  { question: "Is it a planner?", answer: "Yes." },
  { question: "Is it a calendar?", answer: "Almost." },
  { question: "Is it an AI assistant?", answer: "Yes." },
]

export interface HeroFeature {
  iconSrc: string
  text: string
}

export const heroFeatures: HeroFeature[] = [
  { iconSrc: "/marketing/go/icons/one-app.svg", text: "One app for work & personal tasks" },
  { iconSrc: "/marketing/go/icons/offline.svg", text: "Works offline & syncs automatically" },
  {
    iconSrc: "/marketing/go/icons/cross-plattform.svg",
    text: "Available on all your devices",
  },
  { iconSrc: "/marketing/go/icons/ai.svg", text: "AI-powered assistance" },
  { iconSrc: "/marketing/go/icons/collaboration.svg", text: "Real-time collaboration" },
  { iconSrc: "/marketing/go/icons/tag.svg", text: "Fair & flexible pricing" },
]

export interface VideoItem {
  key: string
  title: string
  src: string
  srcMobile?: string
}

export const videos: VideoItem[] = [
  {
    key: "promo",
    title: "Promo Video",
    src: "https://www.youtube.com/embed/ECcCZGkI8Go?rel=0",
    srcMobile: "https://www.youtube.com/embed/27_wCuKQkSw?rel=0",
  },
  {
    key: "howitworks",
    title: "How it works",
    src: "https://www.youtube.com/embed/narxyuHrkkk?si=5TOR_HlOab3Jj9JV&mute=1&rel=0",
  },
]
